import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button } from '../../../components/ui';
import { X, Search, AlertTriangle } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import { formatTimeCR } from '../../../utils/date';

interface TicketResumen {
  id: string;
  total: number;
  metodo_pago: string | null;
  estado: string;
  created_at: string;
  ticket_number?: string | null;
  jugadas: Array<{ num1: string; num2?: string; monto: number; estado: string }>;
}

interface AnularTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  sorteoId: string;
  usuarioId: string;
}

export const AnularTicketModal: React.FC<AnularTicketModalProps> = ({
  isOpen,
  onClose,
  sorteoId,
  usuarioId,
}) => {
  const [tickets, setTickets] = useState<TicketResumen[]>([]);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [anulando, setAnulando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exito, setExito] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Cargar ultimos tickets al abrir
  useEffect(() => {
    if (!isOpen || !sorteoId) return;
    setError(null);
    setExito(null);
    setConfirmId(null);
    setBusqueda('');
    cargarTickets();
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen, sorteoId]);

  const cargarTickets = async () => {
    setLoading(true);
    try {
      const data = await fetchAPI<TicketResumen[]>(`/tickets/ultimos?sorteo_id=${encodeURIComponent(sorteoId)}&limit=10`);
      setTickets(data);
    } catch {
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const ticketsFiltrados = busqueda.trim()
    ? tickets.filter(t =>
        t.id.toLowerCase().includes(busqueda.toLowerCase()) ||
        (t.ticket_number && t.ticket_number.toLowerCase().includes(busqueda.toLowerCase()))
      )
    : tickets;

  const handleAnular = async (ticketId: string) => {
    setAnulando(true);
    setError(null);
    setExito(null);
    try {
      const ticket = tickets.find(t => t.id === ticketId);
      const displayName = ticket?.ticket_number || ticketId;
      await fetchAPI<any>(`/tickets/${encodeURIComponent(ticketId)}/anular`, {
        method: 'POST',
        body: JSON.stringify({ usuario_id: usuarioId }),
      });
      setExito(`Ticket ${displayName} anulado correctamente`);
      setConfirmId(null);
      // Recargar lista
      await cargarTickets();
    } catch (e: any) {
      setError(e.message || 'Error al anular ticket');
    } finally {
      setAnulando(false);
    }
  };

  const formatHora = (iso: string) => {
    try {
      return formatTimeCR(iso);
    } catch {
      return '';
    }
  };

  const estadoBadge = (estado: string) => {
    switch (estado) {
      case 'ABIERTO':
        return 'bg-green-100 text-green-800';
      case 'ANULADO':
        return 'bg-red-100 text-red-800';
      case 'PREMIADO':
        return 'bg-yellow-100 text-yellow-800';
      case 'PAGADO':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Anular Ticket" size="lg">
      <div className="space-y-4">
        {/* Busqueda */}
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            placeholder="Buscar por número de ticket..."
            className="w-full pl-9 pr-3 py-2 border-2 border-gray-300 rounded-lg focus:border-yellow-500 focus:outline-none text-sm"
            autoComplete="off"
          />
        </div>

        {/* Mensajes */}
        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
            <AlertTriangle size={16} />
            {error}
          </div>
        )}
        {exito && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded text-sm">
            {exito}
          </div>
        )}

        {/* Lista de tickets */}
        <div className="max-h-[50vh] overflow-y-auto space-y-2">
          {loading ? (
            <p className="text-center text-gray-500 py-8 text-sm">Cargando...</p>
          ) : ticketsFiltrados.length === 0 ? (
            <p className="text-center text-gray-500 py-8 text-sm">
              {busqueda ? 'No se encontraron tickets' : 'Sin tickets en este sorteo'}
            </p>
          ) : (
            ticketsFiltrados.map(ticket => (
              <div
                key={ticket.id}
                className={`border rounded-lg p-3 transition-all ${
                  ticket.estado === 'ANULADO'
                    ? 'bg-gray-50 border-gray-200 opacity-60'
                    : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-bold text-gray-900 truncate">
                        {ticket.ticket_number || ticket.id}
                      </span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${estadoBadge(ticket.estado)}`}>
                        {ticket.estado}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span>{formatHora(ticket.created_at)}</span>
                      <span>{ticket.metodo_pago || '-'}</span>
                      <span>{ticket.jugadas?.length || 0} jugadas</span>
                      <span className="font-mono font-bold text-gray-700">
                        ${typeof ticket.total === 'number' ? ticket.total.toLocaleString() : ticket.total}
                      </span>
                    </div>
                  </div>

                  {ticket.estado === 'ABIERTO' && (
                    <div className="flex-shrink-0 ml-2">
                      {confirmId === ticket.id ? (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleAnular(ticket.id)}
                            disabled={anulando}
                            isLoading={anulando}
                          >
                            Confirmar
                          </Button>
                          <button
                            onClick={() => setConfirmId(null)}
                            className="p-1 text-gray-400 hover:text-gray-600"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setConfirmId(ticket.id);
                            setError(null);
                            setExito(null);
                          }}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          Anular
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Boton cerrar */}
        <div className="flex justify-end pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AnularTicketModal;
